import { cherry, lightGrey, q4Blue, softGrey, teal, white } from '../resources/materialui-overrides/colors'
import { formatDate } from './date.util'
import { format } from './number.util'

/**
 * Get default headerClass for Ag-grid column definitions
 * @param columnIndex
 * @param typeOfValue
 * @param isAlternating
 * @returns {string}
 */
export const getDefaultHeaderClasses = (columnIndex, typeOfValue, isAlternating) => {
  let className = ''

  if (isAlternating) {
    if (columnIndex % 2 !== 0) {
      className += 'ag-header-cell--even'
    } else {
      className += 'ag-header-cell--odd'
    }
  }

  if (columnIndex === 0) {
    className += ' ag-header-cell--first'
  }

  if (typeOfValue && typeOfValue === 'number') {
    className += ' ag-numeric-header'
  }

  if (typeOfValue && ['boolean', 'date', 'icon', 'centered'].includes(typeOfValue)) {
    className += ' ag-header-cell--centered'
  }

  return className
}

/**
 * Get default cellClassRules for Ag-grid column definitions
 * @param columnIndex
 * @param field
 * @param typeOfValue
 * @param isAlternating
 * @returns {*}
 */
export const getDefaultCellClassRules = (columnIndex, typeOfValue, field, isAlternating = true) => {
  const rules = {
    'ag-column': () => true,
    'ag-column--first': () => columnIndex === 0,
    'ag-column--centered': () => typeOfValue && ['boolean', 'date', 'icon', 'centered'].includes(typeOfValue),
    'ag-column--right': () => typeOfValue && typeOfValue === 'number',
    'ag-column--date': () => typeOfValue && typeOfValue === 'date',
    'ag-column--text': () => typeOfValue && typeOfValue === 'text',
    'ag-column--auto-height': () => typeOfValue && typeOfValue === 'auto-height'
  }

  if (isAlternating) {
    rules['ag-column--odd'] = () => columnIndex % 2 === 0
    rules['ag-column--even'] = () => columnIndex % 2 !== 0
  }

  if (field && field.toLowerCase().indexOf('change') > -1) {
    rules['ag-cell-value--up'] = (params) => params.value > 0
    rules['ag-cell-value--down'] = (params) => params.value < 0
  }

  return rules
}

/**
 * Get Default Column Definition properties
 * @param columnIndex
 * @param typeOfValue
 * @param isAlternating
 * @returns {{headerClass: string, cellClassRules: *, suppressMenu: boolean, suppressMovable: boolean}}
 */
export const getDefaultColumnDef = (columnIndex = 0, typeOfValue = '', isAlternating = true) => {
  return {
    headerClass: getDefaultHeaderClasses(columnIndex, typeOfValue, isAlternating),
    cellClassRules: getDefaultCellClassRules(columnIndex, typeOfValue, '', isAlternating),
    comparator: () => {}, // empty sorting comparator to ignore client sort
    suppressMenu: true,
    suppressMovable: true
  }
}

/**
 * Get Default rowClassRules for Ag-grid
 * @returns {{String: (function(*))}}
 */
export const getDefaultRowClassRules = () => {
  return {
    'ag-row--grouped': (params = {}) => {
      return params.data && params.data.groupedRows && params.data.groupedRows.length
    }
  }
}

/**
 * Get Ag-Grid valueFormatter function for column definitions
 * @param typeOfValue
 */
export const getDefaultValueFormatter = (typeOfValue) => {
  const renderFunctionMap = {
    'boolean': (params) => {
      return typeof params.value === 'boolean' ? params.value ? 'Yes' : 'No' : ''
    },
    'number': (params) => {
      return (Number(params.value) === params.value && params.value % 1 === 0)
        ? format(params.value, 0)
        : format(params.value, 2)
    },
    'date': (params) => {
      const isUTC = true
      const dateFormat = 'MM/DD/YY'

      return params.value ? formatDate(params.value, dateFormat, isUTC) : ''
    }
  }

  return renderFunctionMap[typeOfValue] || null
}

/**
 * Get column width based on filter type
 * @param typeOfValue
 * @returns {number}
 */
export const getDefaultColumnWidth = (typeOfValue) => {
  switch (typeOfValue) {
    case 'string':
      return 270
    case 'choice':
      return 180
    case 'number':
    case 'date':
    case 'boolean':
    case 'bool':
    case 'text':
    default:
      return 120
  }
}

/**
 * Default style for agGrid Export
 * @returns {*}
 */
export const getDefaultExcelStyles = () => {
  return [{
    id: 'title-row',
    font: {
      color: white,
      size: 20
    },
    interior: {
      color: q4Blue,
      pattern: 'Solid'
    }
  }, {
    id: 'subtitle-row',
    font: {
      color: '#000',
      bold: true
    },
    interior: {
      color: white,
      pattern: 'Solid'
    }
  }, {
    id: 'blue-row',
    font: {
      color: white
    },
    interior: {
      color: q4Blue,
      pattern: 'Solid'
    }
  }, {
    id: 'clear-row',
    interior: {
      color: white,
      pattern: 'Solid'
    }
  }, {
    id: 'header',
    interior: {
      color: lightGrey,
      pattern: 'Solid'
    },
    borders: {
      borderBottom: {
        lineStyle: 'Continuous',
        weight: 1,
        color: softGrey
      },
      borderLeft: {
        lineStyle: 'Continuous',
        weight: 1,
        color: softGrey
      },
      borderTop: {
        lineStyle: 'Continuous',
        weight: 1,
        color: softGrey
      },
      borderRight: {
        lineStyle: 'Continuous',
        weight: 1,
        color: softGrey
      }
    }
  }, {
    id: 'ag-cell-value--up',
    font: {
      color: teal
    }
  }, {
    id: 'ag-cell-value--down',
    font: {
      color: cherry
    }
  }, {
    id: 'ag-column--centered',
    alignment: {
      horizontal: 'Center'
    }
  },
  {
    id: 'ag-column--date',
    dataType: 'dateTime',
    numberFormat: {
      format: 'mm-dd-yyyy'
    }
  }, {
    id: 'ag-numeric-cell',
    numberFormat: {
      format: '#,##0'
    }
  }, {
    id: 'ag-cell--decimal',
    numberFormat: {
      format: '0.00'
    }
  }]
}

/**
 * Constructs path using PointerEvent target API.
 * @param event {PointerEvent} - IE11 PointerEvent
 * @param limit {Number} - number of classes in path
 * @return {Array}
 * @private
 */
const _buildTargetPath = (event, limit = 5) => {
  const path = []
  let node = event.target
  let counter = 0

  while (node && node.className && counter < limit) {
    path.push(node)
    node = node.parentNode
    counter++
  }

  return path
}

/**
 * Handle row click event
 * Contains logic to fix AgGrid event propagation and IE11 PointerEvent API.
 * @see { @link: https://github.com/ag-grid/ag-grid/issues/2224 } for further information.
 *
 * @param onRowClick {Function} - callback with on row click logic
 * @param customClickItems {Array} - array of classNames
 */
export const preventDefaultRowClick = (onRowClick, customClickItems) => ({ data = {}, event }) => {
  let path = event.path

  // IE11 doesn't have path in event
  if (!path) {
    path = _buildTargetPath(event)
  }

  // format classNames in path
  path = path
    .map((target) => target.className)
    .filter((target) => target)
    .reduce((acc, curr) => {
      acc.push(...curr.split(' ').map((target) => target.trim()))
      return acc
    }, [])

  if ((customClickItems || []).some((customClickItem) => path.find((target) => target === customClickItem))) {
    return
  }

  onRowClick && onRowClick(data)
}
